import React from 'react';
import BaseScadaNode from './BaseScadaNode';
import { isGateOpen } from './signal_utils';

export function GateIndicator({ x, y, width, open, color_open, color_closed }) {
    if (open === null || open === undefined) return null; // not bound — don't show
    const color = open ? (color_open || '#22C55E') : (color_closed || '#EF4444');
    const gh = 10;
    return (
        <g>
            <rect x={x} y={y} width={width} height={gh} rx={2} fill={color} opacity={0.9} />
            <circle cx={x + 3} cy={y + gh / 2} r={1.5} fill="#0F172A" />
            <circle cx={x + width - 3} cy={y + gh / 2} r={1.5} fill="#0F172A" />
            {open && <rect x={x + width / 2 - 4} y={y + 2} width={8} height={gh - 4} rx={1} fill="#0F172A" opacity={0.5} />}
        </g>
    );
}

export function RibbonBlenderSVG({ config = {}, width = 100, height = 60, showJacket = false, liveValues = {} }) {
    const threshold = parseFloat(config.threshold ?? 0.5);
    const stateVal = liveValues.state;
    const running = stateVal != null && parseFloat(stateVal) >= threshold;
    const color = running ? (config.run_color || '#22C55E') : (config.stop_color || '#64748B');
    const gateCount = parseInt(config.gate_count ?? 0);
    const hasGates = gateCount > 0;

    const cx = width / 2;
    const bodyW = width - 16;
    const bodyH = height * (hasGates ? 0.50 : 0.55);
    const bodyX = 8;
    const bodyY = (height / 2) - bodyH / 2 - (hasGates ? height * 0.04 : 0);

    const sensorType = config.gate_sensor_type || 'single';
    const openLevel  = config.gate_open_level  || 'high';
    const gate1Open  = isGateOpen(liveValues, 'gate_1', 'gate_1_close', sensorType, openLevel, threshold);
    const gate2Open  = isGateOpen(liveValues, 'gate_2', 'gate_2_close', sensorType, openLevel, threshold);
    const gateY = bodyY + bodyH + 2;
    const gateW = Math.min(28, bodyW * 0.38);

    return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: 'block' }}>
            {showJacket && (
                <rect x={bodyX - 4} y={bodyY - 4} width={bodyW + 8} height={bodyH + 8} rx={8}
                    fill="rgba(59,130,246,0.15)" stroke="#3B82F6" strokeWidth={1.5} strokeDasharray="4 2" />
            )}
            <rect x={bodyX} y={bodyY} width={bodyW} height={bodyH} rx={bodyH / 2}
                fill="#1E293B" stroke={color} strokeWidth={2} />
            <path d={`M ${bodyX + 8},${bodyY + 4} Q ${cx - 10},${bodyY + bodyH - 4} ${cx + 10},${bodyY + 4} Q ${bodyX + bodyW - 8},${bodyY + bodyH - 4} ${bodyX + bodyW - 8},${bodyY + bodyH / 2}`}
                stroke={color} strokeWidth={2} fill="none" opacity={0.8} />
            <path d={`M ${bodyX + 8},${bodyY + bodyH - 4} Q ${cx - 10},${bodyY + 4} ${cx + 10},${bodyY + bodyH - 4} Q ${bodyX + bodyW - 8},${bodyY + 4} ${bodyX + bodyW - 8},${bodyY + bodyH / 2}`}
                stroke="#F59E0B" strokeWidth={1.5} fill="none" opacity={0.7} />
            <line x1={bodyX + 12} y1={bodyY + bodyH} x2={bodyX + 8} y2={height - (hasGates ? 16 : 2)} stroke="#475569" strokeWidth={2} />
            <line x1={bodyX + bodyW - 12} y1={bodyY + bodyH} x2={bodyX + bodyW - 8} y2={height - (hasGates ? 16 : 2)} stroke="#475569" strokeWidth={2} />
            <rect x={bodyX - 8} y={(height / 2) - 6 - (hasGates ? height * 0.04 : 0)} width={8} height={12} rx={3} fill="#334155" stroke={color} strokeWidth={1.5} />
            <ellipse cx={cx} cy={bodyY} rx={16} ry={5} fill="#334155" stroke={color} strokeWidth={1.5} />

            {/* 1 gate */}
            {hasGates && gateCount === 1 && (
                <>
                    <line x1={cx} y1={bodyY + bodyH} x2={cx} y2={gateY} stroke="#475569" strokeWidth={4} />
                    <GateIndicator x={cx - gateW / 2} y={gateY} width={gateW} open={gate1Open}
                        color_open={config.on_color} color_closed={config.off_color} />
                </>
            )}
            {/* 2 gates */}
            {hasGates && gateCount >= 2 && (
                <>
                    <line x1={cx - bodyW * 0.22} y1={bodyY + bodyH} x2={cx - bodyW * 0.22} y2={gateY} stroke="#475569" strokeWidth={3} />
                    <GateIndicator x={cx - bodyW * 0.22 - gateW / 2} y={gateY} width={gateW} open={gate1Open}
                        color_open={config.on_color} color_closed={config.off_color} />
                    <line x1={cx + bodyW * 0.22} y1={bodyY + bodyH} x2={cx + bodyW * 0.22} y2={gateY} stroke="#475569" strokeWidth={3} />
                    <GateIndicator x={cx + bodyW * 0.22 - gateW / 2} y={gateY} width={gateW} open={gate2Open}
                        color_open={config.on_color} color_closed={config.off_color} />
                </>
            )}
        </svg>
    );
}

export default function RibbonBlenderNode(props) {
    const { data } = props;
    return (
        <BaseScadaNode {...props}>
            <RibbonBlenderSVG
                config={data?.config}
                width={data?.width || 100}
                height={data?.height || 60}
                liveValues={data?.liveValues || {}}
            />
            {data?.label && (
                <div style={{ textAlign: 'center', fontSize: 10, color: '#94A3B8', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                    {data.label}
                </div>
            )}
        </BaseScadaNode>
    );
}
